import React, { FunctionComponent, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useGetAllDashboardsInfo } from "api/api-hooks/dashboards-hooks";
import { IRedirectButtonProps } from "./DashboardRedirectButtonTypes";

interface Tab {
  uuid: string;
  title: string;
}

export interface ICommonRedirectButtonEditorProps {
  config: IRedirectButtonProps;
  onConfigChange: (newConfig: IRedirectButtonProps) => void;
}

function DashboardRedirectButtonEditor({
  config,
  onConfigChange,
}: ICommonRedirectButtonEditorProps) {
  const conf: IRedirectButtonProps = config ?? {};
  const [selectedDashboard, setSelectedDashboard] = useState<number | null>(
    conf.dashboardId || null
  );
  const [selectedTab, setSelectedTab] = useState<string | null>(conf.uuid || null);
  const [selectedTitle, setSelectedTitle] = useState<string>(conf.title || "");

  const [tabs, setTabs] = useState<{ label: string; uuid: string }[]>([]);
  const { data: dashboards } = useGetAllDashboardsInfo();

  const handleDashboardChange = (e: { value: number }) => {
    const selectedId = e.value;
    setSelectedDashboard(selectedId);

    const currentSelectedDashboard = dashboards?.find(dashboard => dashboard.id === selectedId);
    if (currentSelectedDashboard && currentSelectedDashboard.tabs) {
      const dashboardTabs = currentSelectedDashboard.tabs.map((tab: Tab) => ({
        label: tab.title,
        uuid: tab.uuid,
      }));
      setTabs(dashboardTabs);

      setSelectedTab(null);
      conf.dashboardId = selectedId;
      conf.uuid = null;
      onConfigChange(conf);
    }
  };

  const handleTabChange = (e: { value: any }) => {
    const selectedTabValue = e.value;
    setSelectedTab(selectedTabValue);
    conf.uuid = selectedTabValue.uuid;
    onConfigChange(conf);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setSelectedTitle(newTitle);
    conf.title = newTitle;
    onConfigChange(conf);
  };

  useEffect(() => {
    if (selectedDashboard !== null && dashboards) {
      const currentSelectedDashboard = dashboards.find(
        dashboard => dashboard.id === selectedDashboard
      );
      if (currentSelectedDashboard && currentSelectedDashboard.tabs) {
        const dashboardTabs = currentSelectedDashboard.tabs.map((tab: Tab) => ({
          label: tab.title,
          uuid: tab.uuid,
        }));
        setTabs(dashboardTabs);
      }
    }
  }, [selectedDashboard, dashboards]);

  return (
    <div className="flex flex-column gap-2">
      <label htmlFor="dashboard">{T("action.select-dashboard-to-redirect")}</label>
      <Dropdown
        id="dashboard"
        options={dashboards?.map(dashboard => ({ label: dashboard.title, value: dashboard.id }))}
        value={selectedDashboard}
        onChange={e => handleDashboardChange(e)}
        placeholder={T("action.select-a-dashboard")}
      />
      {selectedDashboard !== null && (
        <>
          <label htmlFor="tab">{T("action.select-tab")}</label>
          <Dropdown
            id="tab"
            options={tabs}
            value={selectedTab}
            onChange={e => handleTabChange(e)}
            placeholder={T("action.select-tab")}
          />
        </>
      )}
      <label htmlFor="title">{T("label.button-title")}</label>
      <InputText
        id="title"
        value={selectedTitle}
        onChange={handleTitleChange}
        placeholder={T("action.enter-button-title")}
      />
    </div>
  );
}

export default DashboardRedirectButtonEditor as FunctionComponent;
